import React, { useState, useEffect } from "react";
import { Palette, Save } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  updatePlatformFontFamily,
  updatePlatformPlaceholderImage,
  updatePlatformPrimaryColor,
  updatePlatformMenuColors,
} from "../../services/api";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const hexToRgbChannels = (hex: string): string => {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return r
    ? `${parseInt(r[1], 16)} ${parseInt(r[2], 16)} ${parseInt(r[3], 16)}`
    : "0 0 0";
};

const applyPrimaryColor = (hex: string) =>
  document.documentElement.style.setProperty(
    "--color-main-rgb",
    hexToRgbChannels(hex),
  );

const FONT_OPTIONS = [
  { label: "Figtree", value: "Figtree" },
  { label: "Outfit", value: "Outfit" },
  { label: "Poppins", value: "Poppins" },
  { label: "Inter", value: "Inter" },
  { label: "DM Sans", value: "DM Sans" },
  { label: "Montserrat", value: "Montserrat" },
  { label: "Nunito", value: "Nunito" },
  { label: "Roboto", value: "Roboto" },
];

const Theme: React.FC = () => {
  const {
    fontFamily: contextFontFamily,
    setFontFamily: setContextFontFamily,
    placeholderImage: contextPlaceholderImage,
    setPlaceholderImage: setContextPlaceholderImage,
    primaryColor: contextPrimaryColor,
    setPrimaryColor: setContextPrimaryColor,
    categoryNameColor: contextCategoryNameColor,
    setCategoryNameColor: setContextCategoryNameColor,
    itemNameColor: contextItemNameColor,
    setItemNameColor: setContextItemNameColor,
    priceColor: contextPriceColor,
    setPriceColor: setContextPriceColor,
    descriptionColor: contextDescriptionColor,
    setDescriptionColor: setContextDescriptionColor,
    categoryTabColor: contextCategoryTabColor,
    setCategoryTabColor: setContextCategoryTabColor,
    CategoryTabText: contextCategoryTabText,
    setCategoryTabText: setContextCategoryTabText,
    CategoryTabUnselectedBg: contextCategoryTabUnselectedBg,
    setCategoryTabUnselectedBg: setContextCategoryTabUnselectedBg,
    CategorySelectedText: contextCategorySelectedText,
    setCategorySelectedText: setContextCategorySelectedText,
  } = useAuth();

  const [selectedFont, setSelectedFont] = useState<string>(contextFontFamily);
  const [selectedColor, setSelectedColor] = useState<string>(contextPrimaryColor);
  const [catNameColor, setCatNameColor] = useState<string>(contextCategoryNameColor);
  const [itemColor, setItemColor] = useState<string>(contextItemNameColor);
  const [priceColor, setPriceColor] = useState<string>(contextPriceColor || contextItemNameColor);
  const [descColor, setDescColor] = useState<string>(contextDescriptionColor);
  const [categoryTabColor, setCategoryTabColor] = useState<string>(contextCategoryTabColor);
  const [categoryTabText, setCategoryTabText] = useState<string>(contextCategoryTabText);
  const [categoryTabUnselectedBg, setCategoryTabUnselectedBg] = useState<string>(contextCategoryTabUnselectedBg);
  const [categorySelectedText, setCategorySelectedText] = useState<string>(contextCategorySelectedText);
  const [placeholderFile, setPlaceholderFile] = useState<File | null>(null);
  const [placeholderPreview, setPlaceholderPreview] = useState<string>(contextPlaceholderImage);
  const [placeholderUploading, setPlaceholderUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSelectedFont(contextFontFamily); }, [contextFontFamily]);
  useEffect(() => { setSelectedColor(contextPrimaryColor); }, [contextPrimaryColor]);
  useEffect(() => { setPlaceholderPreview(contextPlaceholderImage); }, [contextPlaceholderImage]);
  useEffect(() => { setCatNameColor(contextCategoryNameColor); }, [contextCategoryNameColor]);
  useEffect(() => { setItemColor(contextItemNameColor); }, [contextItemNameColor]);
  useEffect(() => { setPriceColor(contextPriceColor); }, [contextPriceColor]);
  useEffect(() => { setDescColor(contextDescriptionColor); }, [contextDescriptionColor]);
  useEffect(() => { setCategoryTabColor(contextCategoryTabColor); }, [contextCategoryTabColor]);
  useEffect(() => { setCategoryTabText(contextCategoryTabText); }, [contextCategoryTabText]);
  useEffect(() => { setCategoryTabUnselectedBg(contextCategoryTabUnselectedBg); }, [contextCategoryTabUnselectedBg]);
  useEffect(() => { setCategorySelectedText(contextCategorySelectedText); }, [contextCategorySelectedText]);

  const uploadPlaceholderImage = async (): Promise<string> => {
    if (!placeholderFile) return placeholderPreview;
    const formDataObj = new FormData();
    formDataObj.append("image", placeholderFile);
    setPlaceholderUploading(true);
    try {
      const res = await api.post<{ url: string }>("/upload-image", formDataObj, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPlaceholderUploading(false);
      setPlaceholderFile(null);
      return res.data.url;
    } catch {
      setPlaceholderUploading(false);
      toast.error("Placeholder image upload failed");
      return placeholderPreview;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedFont !== contextFontFamily) {
        await updatePlatformFontFamily(selectedFont);
        setContextFontFamily(selectedFont);
        document.documentElement.style.setProperty("--app-font", selectedFont);
      }

      if (selectedColor !== contextPrimaryColor) {
        await updatePlatformPrimaryColor(selectedColor);
        setContextPrimaryColor(selectedColor);
        applyPrimaryColor(selectedColor);
      }

      if (
        catNameColor !== contextCategoryNameColor ||
        itemColor !== contextItemNameColor ||
        priceColor !== contextPriceColor ||
        descColor !== contextDescriptionColor ||
        categoryTabColor !== contextCategoryTabColor ||
        categoryTabText !== contextCategoryTabText ||
        categoryTabUnselectedBg !== contextCategoryTabUnselectedBg ||
        categorySelectedText !== contextCategorySelectedText
      ) {
        await updatePlatformMenuColors({
          categoryNameColor: catNameColor,
          itemNameColor: itemColor,
          priceColor,
          descriptionColor: descColor,
          categoryTabColor,
          categoryTabText,
          categoryTabUnselectedBg,
          categorySelectedText,
        });
        setContextCategoryNameColor(catNameColor);
        setContextItemNameColor(itemColor);
        setContextPriceColor(priceColor);
        setContextDescriptionColor(descColor);
        setContextCategoryTabColor(categoryTabColor);
        setContextCategoryTabText(categoryTabText);
        setContextCategoryTabUnselectedBg(categoryTabUnselectedBg);
        setContextCategorySelectedText(categorySelectedText);
        document.documentElement.style.setProperty("--color-category-name", catNameColor);
        document.documentElement.style.setProperty("--color-item-name", itemColor);
        document.documentElement.style.setProperty("--color-price", priceColor);
        document.documentElement.style.setProperty("--color-description", descColor);
        document.documentElement.style.setProperty("--color-category-tab", categoryTabColor);
        document.documentElement.style.setProperty("--color-category-tab-text", categoryTabText);
        document.documentElement.style.setProperty("--color-category-selected-text", categorySelectedText);
        document.documentElement.style.setProperty("--color-category-tab-unselected-bg", categoryTabUnselectedBg);
      }

      const uploadedPlaceholder = await uploadPlaceholderImage();
      if (uploadedPlaceholder !== contextPlaceholderImage) {
        await updatePlatformPlaceholderImage(uploadedPlaceholder);
        setContextPlaceholderImage(uploadedPlaceholder);
        setPlaceholderPreview(uploadedPlaceholder);
      }

      toast.success("Theme saved successfully!");
    } catch {
      toast.error("Failed to save theme.");
    } finally {
      setSaving(false);
    }
  };

  const colorFields = [
    { label: "Category Name", value: catNameColor, set: setCatNameColor, varName: "--color-category-name" },
    { label: "Item Name", value: itemColor, set: setItemColor, varName: "--color-item-name" },
    { label: "Price", value: priceColor, set: setPriceColor, varName: "--color-price" },
    { label: "Description", value: descColor, set: setDescColor, varName: "--color-description" },
    { label: "Category Tab Selected Background", value: categoryTabColor, set: setCategoryTabColor, varName: "--color-category-tab" },
    { label: "Category Tab Selected Text Color", value: categorySelectedText, set: setCategorySelectedText, varName: "--color-category-selected-text" },
    { label: "Category Tab Text", value: categoryTabText, set: setCategoryTabText, varName: "--color-category-tab-text" },
    { label: "Category Tab Unselected Background", value: categoryTabUnselectedBg, set: setCategoryTabUnselectedBg, varName: "--color-category-tab-unselected-bg" },
  ] as { label: string; value: string; set: (v: string) => void; varName: string }[];

  return (
    <div className="max-w-3xl mx-auto p-3 pt-0 sm:p-4 lg:p-8">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-4 sm:py-6 mb-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Palette size={22} className="text-blue-600 shrink-0" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-0 truncate">Theme</h1>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || placeholderUploading}
            className="shrink-0 px-3 py-2 sm:px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition inline-flex items-center gap-2 text-sm sm:text-base whitespace-nowrap disabled:opacity-60"
          >
            <Save size={15} />
            {saving ? "Saving..." : "Save Theme"}
          </button>
        </div>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {/* Font Family */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-3 sm:space-y-4 shadow-sm">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-1">Font Family</h2>
            <p className="text-xs sm:text-sm text-gray-500">Sets the font used across the entire platform.</p>
          </div>
          <select
            value={selectedFont}
            onChange={(e) => {
              setSelectedFont(e.target.value);
              document.documentElement.style.setProperty("--app-font", e.target.value);
            }}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            style={{ fontFamily: selectedFont }}
          >
            {FONT_OPTIONS.map((f) => (
              <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                {f.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-400" style={{ fontFamily: selectedFont }}>
            Preview: The quick brown fox jumps over the lazy dog
          </p>
        </div>

        {/* Primary Color */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-3 sm:space-y-4 shadow-sm">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-1">Primary Color</h2>
            <p className="text-xs sm:text-sm text-gray-500">Used for buttons and accents across the POS.</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={selectedColor}
              onChange={(e) => {
                setSelectedColor(e.target.value);
                applyPrimaryColor(e.target.value);
              }}
              className="h-10 w-12 cursor-pointer rounded border border-gray-300 p-0.5 bg-white shrink-0"
            />
            <input
              type="text"
              value={selectedColor}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedColor(val);
                if (/^#[0-9A-Fa-f]{6}$/.test(val)) applyPrimaryColor(val);
              }}
              maxLength={7}
              placeholder="#000000"
              className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <span
              className="h-9 w-9 rounded-full border border-gray-200 shadow-sm shrink-0"
              style={{ backgroundColor: selectedColor }}
            />
          </div>
        </div>

        {/* Menu Text Colors */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-3 sm:space-y-4 shadow-sm">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-1">Menu Text Colors</h2>
            <p className="text-xs sm:text-sm text-gray-500">
              Customize category name, item name/price, description, and tab colors.
            </p>
          </div>
          <div className="space-y-4">
            {colorFields.map(({ label, value, set, varName }) => (
              <div key={varName} className="flex flex-col gap-1.5">
                <span className="text-xs sm:text-sm font-medium text-gray-600">{label}</span>
                <div className="flex items-center gap-2 sm:gap-3">
                  <input
                    type="color"
                    value={value}
                    onChange={(e) => {
                      set(e.target.value);
                      document.documentElement.style.setProperty(varName, e.target.value);
                    }}
                    className="h-10 w-12 cursor-pointer rounded border border-gray-300 p-0.5 bg-white shrink-0"
                  />
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => {
                      set(e.target.value);
                      if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value))
                        document.documentElement.style.setProperty(varName, e.target.value);
                    }}
                    maxLength={7}
                    placeholder="#000000"
                    className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono text-gray-800 focus:border-blue-500 focus:outline-none"
                  />
                  <span
                    className="h-9 w-9 rounded-full border border-gray-200 shadow-sm shrink-0"
                    style={{ backgroundColor: value }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Placeholder Image */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-3 sm:space-y-4 shadow-sm">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-1">Menu Placeholder Image</h2>
            <p className="text-xs sm:text-sm text-gray-500">
              Default image shown for menu items without their own image.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            {placeholderPreview && (
              <div className="relative self-start">
                <img
                  src={placeholderPreview}
                  alt="Placeholder preview"
                  className="h-20 w-20 rounded-lg object-cover border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => {
                    setPlaceholderPreview("");
                    setPlaceholderFile(null);
                  }}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white text-[11px] flex items-center justify-center hover:bg-red-600"
                >
                  ✕
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/webp"
              className="w-full p-2.5 border-2 border-dashed border-gray-300 rounded-md text-sm
                file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0
                file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setPlaceholderFile(f);
                  setPlaceholderPreview(URL.createObjectURL(f));
                }
              }}
            />
          </div>
          {placeholderUploading && (
            <p className="text-blue-600 text-xs">Uploading image...</p>
          )}
        </div>

        {/* Bottom spacer for mobile scroll */}
        <div className="h-4 sm:h-0" />
      </div>
    </div>
  );
};

export default Theme;
